import { createContext, useContext, useEffect, useRef, ReactNode } from "react";
import { useAuth } from "../../contexts/AuthContext";

interface PendoContextValue {
  track: (event: string, properties?: Record<string, unknown>) => void;
}

const PendoContext = createContext<PendoContextValue>({ track: () => {} });

const PENDO_API_KEY = import.meta.env.VITE_PENDO_API_KEY;

export function PendoProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, userEmail } = useAuth();
  const initialized = useRef(false);

  // Load the Pendo agent stub once on mount.
  // The stub queues calls until the CDN script finishes loading.
  useEffect(() => {
    if (!PENDO_API_KEY) return;

    const p = (window.pendo = window.pendo || {});
    p._q = p._q || [];
    const methods = [
      "initialize",
      "identify",
      "updateOptions",
      "pageLoad",
      "track",
      "trackAgent",
    ] as const;
    for (const m of methods) {
      if (!p[m]) {
        // initialize is unshifted so it always processes first when the agent loads
        p[m] = (...args: unknown[]) =>
          p._q[m === "initialize" ? "unshift" : "push"]([m, ...args]);
      }
    }
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://cdn.pendo.io/agent/static/${PENDO_API_KEY}/pendo.js`;
    document.head.appendChild(script);
  }, []);

  // Initialize Pendo identity once the user is authenticated.
  useEffect(() => {
    if (!isAuthenticated || !userEmail || initialized.current) return;
    const tenantId = localStorage.getItem("tenantId") ?? "unknown";
    window.pendo?.initialize({
      visitor: { id: userEmail, email: userEmail },
      account: { id: tenantId },
    });
    initialized.current = true;
  }, [isAuthenticated, userEmail]);

  // Clear Pendo session on logout.
  useEffect(() => {
    if (!isAuthenticated && initialized.current) {
      window.pendo?.clearSession?.();
      initialized.current = false;
    }
  }, [isAuthenticated]);

  return (
    <PendoContext.Provider
      value={{ track: (e, p) => window.pendo?.track(e, p) }}
    >
      {children}
    </PendoContext.Provider>
  );
}

export const usePendo = () => useContext(PendoContext);
